import { GOOGLE_RECAPTCHA_SITE_KEY } from '@/base/constants/ConfigVars'

const siteKey = GOOGLE_RECAPTCHA_SITE_KEY
let scriptEl = null

/**
 * Dynamically loads the Google reCaptcha Enterprise script if not already loaded.
 *
 * @returns {Promise<void>} Resolves once the script is successfully loaded.
 * @throws {Error} If the script fails to load.
 */
export async function loadRecaptchaScript() {
  return new Promise((resolve, reject) => {
    // If already loaded, resolve immediately
    if (window.grecaptcha && window.grecaptcha.enterprise)
      return resolve()

    scriptEl = document.createElement('script')
    scriptEl.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`
    scriptEl.async = true
    scriptEl.defer = true
    scriptEl.onload = () => {
      resolve()
    }
    scriptEl.onerror = () => {
      reject(new Error('Failed to load reCaptcha Enterprise script'))
    }
    document.head.appendChild(scriptEl)
  })
}

/**
 * Cleans up the loaded reCaptcha Enterprise script and removes the global `grecaptcha` object.
 */
export function cleanupRecaptcha() {
  // Remove reCaptcha badge from DOM
  const badge = document.querySelector('.grecaptcha-badge')
  if (badge) {
    badge.parentNode.removeChild(badge)
  }

  // Remove the script tag
  if (scriptEl && scriptEl.parentNode) {
    scriptEl.parentNode.removeChild(scriptEl)
    scriptEl = null
  }

  // Clean up the grecaptcha instance and global reference
  if (window.grecaptcha) {
    delete window.grecaptcha
  }
}

/**
 * Executes a reCaptcha Enterprise action and returns the generated token.
 *
 * @param {string} action - The action to associate with the reCaptcha token (e.g., 'login', 'register').
 * @returns {Promise<string>} A promise that resolves to the reCaptcha token.
 * @throws {Error} If reCaptcha is not loaded.
 */
export async function executeRecaptcha(action) {
  if (!window.grecaptcha || !window.grecaptcha.enterprise) {
    throw new Error('EXECUTE_RECAPTCHA_FAILED')
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha.enterprise.ready(() => {
      window.grecaptcha.enterprise.execute(siteKey, { action })
        .then(token => resolve(token))
        .catch(err => reject(err))
    })
  })
}
